import { GraphQLResult } from '@aws-amplify/api';
import {
  GeneratedMutation,
  GeneratedQuery,
  GraphqlSubscriptionMessage
} from '@aws-amplify/api-graphql';
import { Observable as ZenObservable } from 'zen-observable-ts';

import { client } from '@/utils/amplifyClient';
import { notifyError } from '@/utils/bugsnag';
import { getTokensFromCookies } from '@/utils/helpers/auth';

type AuthMode = 'apiKey' | 'lambda';

type QueryOptions<TInput, TOutput> = {
  query: GeneratedQuery<TInput, TOutput>;
  variables?: TInput;
  authMode?: AuthMode;
};

type MutationOptions<TInput, TOutput> = {
  query: GeneratedMutation<TInput, TOutput>;
  variables?: TInput;
  authMode?: AuthMode;
};

interface GraphQLError {
  message: string;
  path?: string[];
  locations?: Array<{
    line: number;
    column: number;
    sourceName?: string | null;
  }>;
  errorType?: string;
  errorInfo?: unknown;
  data?: null;
}

const extractOperationName = (queryString: string): string => {
  const operationMatch = queryString.match(/(?:query|mutation)\s*(\w+)?/);

  if (operationMatch && operationMatch[1]) {
    return operationMatch[1];
  }

  const fieldMatch = queryString.match(/\{\s*(\w+)/);

  if (fieldMatch && fieldMatch[1]) {
    return fieldMatch[1];
  }

  return 'UnknownOperation';
};

interface ErrorDetails {
  message: string;
  name: string;
  stack?: string;
}

interface ErrorMetadata {
  operation: string;
  operationName: string;
  variables?: unknown;
  timestamp: string;
  queryType: string;
  environment: string;
  graphQLErrorType: string;
  originalError: unknown;
}

const extractUnknownError = (error: unknown) => ({
  message: String(error.message) || 'Unknown error',
  name: 'UnknownError'
});

const extractGraphQLError = (error: unknown) => {
  const errorObj = error as Record<string, unknown>;
  const hasGraphQLErrors =
    'errors' in errorObj &&
    Array.isArray(errorObj.errors) &&
    errorObj.errors.length > 0;

  if (hasGraphQLErrors) {
    const graphQLError = (errorObj.errors as GraphQLError[])[0];
    return {
      message: graphQLError.message || 'GraphQL Error',
      name: graphQLError.errorType || 'Error',
      stack: errorObj.stack as string
    };
  }

  return null;
};

const extractErrorDetails = (error: unknown): ErrorDetails => {
  if (error instanceof Error) return error;

  if (!error || typeof error !== 'object')
    return { message: String(error), name: 'UnknownError' };

  return extractGraphQLError(error) ?? extractUnknownError(error);
};

const handleGraphQLError = (
  error: unknown,
  operation: string,
  queryString: string,
  variables?: unknown
): void => {
  const operationName = extractOperationName(queryString);
  const { message, name, stack } = extractErrorDetails(error);

  if (
    operationName !== 'CreateMessage' &&
    !error.errors.some(e => e.message.includes('network'))
  ) {
    localStorage.setItem('chat_error', true);
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'chat_error',
        newValue: JSON.stringify(true)
      })
    );
  }

  const graphQLError = new Error(message);
  graphQLError.name = `${operationName}_${name}`;
  graphQLError.stack = stack;

  const metadata: ErrorMetadata = {
    operation,
    operationName,
    variables,
    timestamp: new Date().toISOString(),
    queryType: operation,
    environment: process.env.NODE_ENV || 'development',
    graphQLErrorType: name,
    originalError: error.errors || error
  };

  notifyError(graphQLError, metadata);
};

export const executeQuery = async <TInput, TOutput>(
  options: QueryOptions<TInput, TOutput>,
  headers: Record<string, string> = {}
): Promise<TOutput | undefined> => {
  try {
    const { query, variables } = options;
    const queryString = query.toString();

    const response = (await client.graphql(
      {
        authMode: 'apiKey',
        query: queryString,
        variables: variables as Record<string, unknown>
      },
      {
        'x-api-key': process.env.NEXT_PUBLIC_APPSYNC_API_KEY,
        ...headers
      }
    )) as GraphQLResult<TOutput>;

    return response.data;
  } catch (error) {
    handleGraphQLError(
      error,
      'Query',
      options.query.toString(),
      options.variables
    );
    return undefined;
  }
};

export const executeMutation = async <TInput, TOutput>(
  options: MutationOptions<TInput, TOutput>,
  headers: Record<string, string> = {}
): Promise<TOutput | undefined> => {
  try {
    const { query, variables } = options;
    const queryString = query.toString();

    const response = (await client.graphql(
      {
        authMode: 'apiKey',
        query: queryString,
        variables: variables as Record<string, unknown>
      },
      {
        'x-api-key': process.env.NEXT_PUBLIC_APPSYNC_API_KEY,
        ...headers
      }
    )) as GraphQLResult<TOutput>;

    return response.data;
  } catch (error) {
    handleGraphQLError(
      error,
      'Mutation',
      options.query.toString(),
      options.variables
    );
    return undefined;
  }
};

export async function executeQueryWithJWT<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<GraphQLResult<T>> {
  const { accessToken } = getTokensFromCookies();

  if (!accessToken) {
    throw new Error('JWT token is required for authentication');
  }

  try {
    const response = await client.graphql({
      query,
      variables,
      authMode: 'lambda',
      authToken: accessToken
    });

    const result = response as GraphQLResult<T>;

    if (result.errors) {
      console.warn('GraphQL query returned errors:', result.errors);
    }

    return result;
  } catch (error) {
    console.error('GraphQL query error:', error);

    if (error.errors.errorType !== 'NotFound') {
      handleGraphQLError(error as Error, 'query', query, variables);
    }
  }
}

export const executeMutationWithJWT = async <TData = never, TVariables = never>(
  mutation: string,
  variables?: TVariables,
  options?: {
    onCompleted?: (data: TData) => void;
    onError?: (error: Error) => void;
  }
): Promise<TData | null> => {
  try {
    const { data } = await executeQueryWithJWT(mutation, variables);

    if (data.errors) {
      console.error('Mutation error:', data.errors.type);
      options?.onError?.(data.errors);
      return null;
    }

    options?.onCompleted?.(data as TData);
    return data as TData;
  } catch (error) {
    console.error('Mutation execution error:', error);
    options?.onError?.(error as Error);
    return null;
  }
};

export const executeSubscribeWithJWT = <TData>(
  queryString: string,
  onNext: (data: TData) => Promise<void> | void,
  onError: (error: unknown) => void = console.error
): (() => void) => {
  const { accessToken } = getTokensFromCookies();

  const source = client.graphql(
    {
      query: queryString,
      authMode: 'lambda',
      authToken: accessToken
    },
    {}
  ) as unknown as ZenObservable<GraphqlSubscriptionMessage<TData>>;

  const subscription = source.subscribe({
    next: value => {
      if (value?.data) {
        onNext(value.data as TData);
      }
    },
    error: err => {
      onError(err);
    }
  });

  return () => {
    subscription.unsubscribe();
  };
};
